import React, { useState, useEffect, useCallback } from 'react';
import { IUserJson } from '@scale/shared/types/user';
import { QualificationType } from '@scale/shared/types/qualification/types';
import SelectQualification from 'react-scale/corp/components/SelectQualification';
import { Typography, Spinner, Button } from '@scale/scaleui';
import apiFetch from 'react-scale/lib/api-fetch';
import TaskerScreeningInstructionsModal from 'react-scale/corp/views/rapid/TaskerScreeningUtils/TaskerScreeningInstructionsModal';
import { WorkerSkillDimension } from '@scale/shared-types';
import { QualificationSelectOption } from '@scale/shared/types/params/course';

interface TaskerScreeningCorpSettingsProps {
  user: IUserJson;
}

const TaskerScreeningCorpSettings: React.FC<TaskerScreeningCorpSettingsProps> = () => {
  const [qualificationOption, setQualificationOption] = useState<QualificationSelectOption>({
    qualificationType: QualificationType.WorkerSkill,
    workerSkill: {
      dimension: '',
      label: '',
      value: '',
    },
  });
  const handleUpdateWorkerSkill = useCallback(
    (value: any) => {
      const updatedQualOption = qualificationOption;
      if (Object.values(WorkerSkillDimension).includes(value)) {
        if (!updatedQualOption.workerSkill) {
          updatedQualOption.workerSkill = {
            label: '',
            value: '',
            dimension: value,
          };
        } else {
          updatedQualOption.workerSkill.dimension = value;
        }
      } else {
        updatedQualOption.workerSkill = {
          ...updatedQualOption.workerSkill,
          ...value,
        };
      }
      setQualificationOption({ ...updatedQualOption });
    },
    [qualificationOption, setQualificationOption],
  );
  const [instructionsUrl, setInstructionsUrl] = useState<string>();
  const [loadingInstructions, setLoadingInstructions] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [updatingInstructions, setUpdatingInstructions] = useState<boolean>(false);

  const fetchQualificationInstructionsUrl = async (
    workerSkillId: string,
    workerSkillDimension: string,
  ) => {
    try {
      const query = {
        workerSkillId,
        workerSkillDimension,
        qualificationType: QualificationType.WorkerSkill,
      };
      const { qualifications: fetchedQualifications } = await apiFetch(
        null,
        `corp-api/find/qualifications`,
        { query },
      );
      setInstructionsUrl(fetchedQualifications[0].instructionsUrl);
    } catch (error) {
      // do nothing
    }
  };

  useEffect(() => {
    if (
      qualificationOption?.workerSkill?.value?.length &&
      qualificationOption?.workerSkill?.dimension?.length
    ) {
      setLoadingInstructions(true);
      fetchQualificationInstructionsUrl(
        qualificationOption.workerSkill.value,
        qualificationOption.workerSkill.dimension,
      );
      setLoadingInstructions(false);
    }
  }, [qualificationOption?.workerSkill?.value, qualificationOption?.workerSkill?.dimension]);

  return (
    <div className="scaleui scale-theme scale-tailwind p-4">
      <div>
        <Typography.H4>Setting Skill Instructions</Typography.H4>
        <p>
          Use this tool to set PDF instructions corresponding with worker skills. Buttons to view
          these instructions will appear while auditing answers for the related skill.
        </p>
        <div className="w-[400px] mt-4">
          <SelectQualification
            value={qualificationOption}
            onWorkerSkillChange={handleUpdateWorkerSkill}
            qualificationType={QualificationType.WorkerSkill}
            requireStatus={false}
          />
        </div>
        <div className="mt-4 flex flex-row">
          {loadingInstructions && <Spinner size="sm" />}
          {qualificationOption?.workerSkill?.value &&
            qualificationOption?.workerSkill?.dimension &&
            instructionsUrl && (
              <Button variant="secondary" className="mr-4" onClick={() => setModalOpen(true)}>
                Preview Instructions
              </Button>
            )}
          <Button
            variant="secondary"
            onClick={() => {
              setModalOpen(true);
              setUpdatingInstructions(true);
            }}
            disabled={
              !qualificationOption?.workerSkill?.value &&
              !qualificationOption?.workerSkill?.dimension
            }
          >
            Update Instructions
          </Button>
          <TaskerScreeningInstructionsModal
            open={modalOpen}
            workerSkillId={qualificationOption?.workerSkill?.value}
            workerSkillDimension={qualificationOption?.workerSkill?.dimension}
            workerSkillName={qualificationOption?.workerSkill?.label}
            onClose={() => {
              setModalOpen(false);
              setUpdatingInstructions(false);
            }}
            updatingInstructions={updatingInstructions}
            title={
              updatingInstructions ? 'Upload Skill Instructions' : 'Preview Skill Instructions'
            }
            instructionsUrl={instructionsUrl}
            setInstructionsUrl={setInstructionsUrl}
          />
        </div>
      </div>
    </div>
  );
};

export default TaskerScreeningCorpSettings;
